/**
 * Persisted mapping: bridge root (absolute) → stable folder name under the worktrees root.
 * On disk the JSON key remains `launcherCwds` for backward compatibility with existing installs.
 * Names use the last path segment plus a numeric suffix when needed (e.g. myapp, myapp-2).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import os from 'node:os';

export const LAYOUT_FILENAME = 'worktree-launcher-layout.json';

/** On-disk shape (legacy key `launcherCwds`); bridge root is stored in `absolutePath`. */
export type WorktreeLayoutFile = { launcherCwds: Array<{ absolutePath: string; dirName: string }> };

export function defaultWorktreeLayoutPath(): string {
  return path.join(os.homedir(), '.buildautomaton', LAYOUT_FILENAME);
}

function normalizeLoadedLayout(raw: unknown): WorktreeLayoutFile {
  if (raw && typeof raw === 'object' && 'launcherCwds' in raw) {
    const j = raw as { launcherCwds?: unknown };
    if (Array.isArray(j.launcherCwds)) return { launcherCwds: j.launcherCwds as WorktreeLayoutFile['launcherCwds'] };
  }
  return { launcherCwds: [] };
}

export function loadWorktreeLayout(): WorktreeLayoutFile {
  try {
    const p = defaultWorktreeLayoutPath();
    if (!fs.existsSync(p)) return { launcherCwds: [] };
    const raw = JSON.parse(fs.readFileSync(p, 'utf8')) as unknown;
    return normalizeLoadedLayout(raw);
  } catch {
    return { launcherCwds: [] };
  }
}

export function saveWorktreeLayout(layout: WorktreeLayoutFile): void {
  try {
    const dir = path.dirname(defaultWorktreeLayoutPath());
    fs.mkdirSync(dir, { recursive: true });
    fs.writeFileSync(defaultWorktreeLayoutPath(), JSON.stringify(layout, null, 2), 'utf8');
  } catch {
    /* ignore */
  }
}

export function baseNameSafe(pathString: string): string {
  return path.basename(pathString).replace(/[^a-zA-Z0-9._-]+/g, '-') || 'cwd';
}

/** Read-only: stable folder name for this bridge root if already allocated, else undefined. */
export function getLauncherDirNameIfPresent(layout: WorktreeLayoutFile, bridgeRootPath: string): string | undefined {
  const norm = path.resolve(bridgeRootPath);
  const existing = layout.launcherCwds.find((e) => path.resolve(e.absolutePath) === norm);
  return existing?.dirName;
}

/** Mutates `layout` and persists when a new bridge root entry is added. */
export function allocateDirNameForLauncherCwd(layout: WorktreeLayoutFile, bridgeRootPath: string): string {
  const existing = getLauncherDirNameIfPresent(layout, bridgeRootPath);
  if (existing) return existing;
  const norm = path.resolve(bridgeRootPath);
  const base = baseNameSafe(norm);
  const used = new Set(layout.launcherCwds.map((e) => e.dirName));
  let name = base;
  let n = 2;
  while (used.has(name)) {
    name = `${base}-${n}`;
    n += 1;
  }
  layout.launcherCwds.push({ absolutePath: norm, dirName: name });
  saveWorktreeLayout(layout);
  return name;
}
